/**
 * Optional preprocessing utility: grayscale → percentile contrast stretch →
 * unsharp mask. NOT part of the scan loop: the efficacy benchmark
 * (tools/benchmark/enhance-test.mjs) measured zero recovered reads on
 * blur-limited frames — the engine's own binarizer already handles contrast,
 * and defocus blur must be solved optically (auto-zoom, focus), not by
 * sharpening. Exported for consumers who want it for display/preprocessing.
 */

function makeImageData(width: number, height: number): ImageData {
  if (typeof ImageData !== "undefined") {
    return new ImageData(width, height);
  }
  // Node (tests/benchmarks): structural stand-in
  return {
    data: new Uint8ClampedArray(width * height * 4),
    width,
    height,
    colorSpace: "srgb",
  } as ImageData;
}

export function enhance(src: ImageData): ImageData {
  const { width: w, height: h, data } = src;
  const n = w * h;

  const gray = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const j = i * 4;
    gray[i] = 0.299 * data[j] + 0.587 * data[j + 1] + 0.114 * data[j + 2];
  }

  // Contrast stretch across the 2nd..98th percentile.
  const hist = new Uint32Array(256);
  for (let i = 0; i < n; i++) hist[gray[i] & 255]++;
  let lo = 0, hi = 255, acc = 0;
  for (let v = 0; v < 256; v++) {
    acc += hist[v];
    if (acc >= n * 0.02) { lo = v; break; }
  }
  acc = 0;
  for (let v = 0; v < 256; v++) {
    acc += hist[v];
    if (acc >= n * 0.98) { hi = v; break; }
  }
  const range = Math.max(1, hi - lo);
  for (let i = 0; i < n; i++) {
    gray[i] = ((gray[i] - lo) * 255) / range;
  }

  // Separable radius-1 box blur as the unsharp base.
  const tmp = new Float32Array(n);
  const blur = new Float32Array(n);
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      const l = x > 0 ? gray[row + x - 1] : gray[row + x];
      const r = x < w - 1 ? gray[row + x + 1] : gray[row + x];
      tmp[row + x] = (l + gray[row + x] + r) / 3;
    }
  }
  for (let y = 0; y < h; y++) {
    const up = y > 0 ? y - 1 : y;
    const dn = y < h - 1 ? y + 1 : y;
    for (let x = 0; x < w; x++) {
      blur[y * w + x] = (tmp[up * w + x] + tmp[y * w + x] + tmp[dn * w + x]) / 3;
    }
  }

  const AMOUNT = 1.4;
  const out = makeImageData(w, h);
  const od = out.data;
  for (let i = 0; i < n; i++) {
    const sharp = gray[i] + AMOUNT * (gray[i] - blur[i]);
    const v = sharp < 0 ? 0 : sharp > 255 ? 255 : sharp;
    const j = i * 4;
    od[j] = od[j + 1] = od[j + 2] = v;
    od[j + 3] = 255;
  }
  return out;
}
